import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AccountProvider } from './context/AccountContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import AuthCallbackPage from './components/auth/AuthCallbackPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import UnifiedSearchPage from './components/UnifiedSearchPage';
import BookingDetails from './components/BookingDetails';
import HotelBookingCheckout from './components/hotels/HotelBookingCheckout';
import AdminLogin from './components/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminEntity from './components/admin/AdminEntity';
import AdminBookings from './components/admin/AdminBookings';
import AdminInvoices from './components/admin/AdminInvoices';
import { AdminCustomers, AdminCustomerProfile, AdminCorporates, AdminCorporateProfile } from './components/admin/AdminCrm';
import AdminCrmAnalytics from './components/admin/AdminCrmAnalytics';
import AdminRateHawk from './components/admin/AdminRateHawk';
import AdminETGOperations from './components/admin/AdminETGOperations';
import AdminSettings from './components/admin/AdminSettings';
import AdminTravelPartners from './components/admin/AdminTravelPartners';
import AdminPartners from './components/admin/AdminPartners';
import AdminModules from './components/admin/AdminModules';
import AdminComparison from './components/admin/AdminComparison';
import AdminThemes from './components/admin/AdminThemes';
import AdminPaymentGateways from './components/admin/AdminPaymentGateways';
import AnalyticsDashboard from './components/admin/AnalyticsDashboard';
import ProtectedRoute from './components/admin/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Terms from './components/legal/Terms';
import Privacy from './components/legal/Privacy';
import Refund from './components/legal/Refund';
import Cookie from './components/legal/Cookie';
import About from './components/legal/About';
import Contact from './components/legal/Contact';
import Faq from './components/legal/Faq';
import MyBookings from './components/account/MyBookings';
import BookingDetailPage from './components/account/BookingDetailPage';
import Wallet from './components/account/Wallet';
import SavedTravellers from './components/account/SavedTravellers';
import WishlistPage from './components/account/Wishlist';
import TripShare from './components/account/TripShare';
import HotelDetailPage from './components/hotels/HotelDetailPage';
import HotelDestinationSEO from './components/hotels/HotelDestinationSEO';
import AdminUsers from './components/admin/AdminUsers';
import AdminSystemHealth from './components/admin/AdminSystemHealth';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { bootAnalytics } from './lib/analytics';

bootAnalytics();

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <CustomerAuthProvider>
            <CurrencyProvider>
              <AccountProvider>
              <BrowserRouter>
                <ErrorBoundary>
                  <Routes>
                  {/* Admin routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="bookings" element={<AdminBookings />} />
                    <Route path="invoices" element={<AdminInvoices />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="customers/:email" element={<AdminCustomerProfile />} />
                    <Route path="corporates" element={<AdminCorporates />} />
                    <Route path="corporates/:company" element={<AdminCorporateProfile />} />
                    <Route path="crm-analytics" element={<AdminCrmAnalytics />} />
                    <Route path="travel-partners" element={<AdminPartners />} />
                    <Route path="ratehawk" element={<AdminRateHawk />} />
                    <Route path="etg-operations" element={<AdminETGOperations />} />
                    <Route path="travel-partners/legacy" element={<AdminTravelPartners />} />
                    <Route path="modules" element={<AdminModules />} />
                    <Route path="comparison" element={<AdminComparison />} />
                    <Route path="themes" element={<AdminThemes />} />
                    <Route path="payment-gateways" element={<AdminPaymentGateways />} />
                    <Route path="analytics" element={<AnalyticsDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="system-health" element={<AdminSystemHealth />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path=":entity" element={<AdminEntity />} />
                  </Route>

                  {/* Google auth callback */}
                  <Route path="/auth/google/callback" element={<AuthCallbackPage />} />

                  {/* Public routes — single unified search page handles all 12 modules */}
                  <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
                  <Route path="/:type/search" element={<PublicLayout><UnifiedSearchPage /></PublicLayout>} />
                  <Route path="/hotels/:id" element={<PublicLayout><HotelDetailPage /></PublicLayout>} />
                  <Route path="/hotels-in/:slug" element={<PublicLayout><HotelDestinationSEO /></PublicLayout>} />
                  <Route path="/booking/hotels/:id" element={<PublicLayout><HotelBookingCheckout /></PublicLayout>} />
                  <Route path="/booking/:type/:id" element={<PublicLayout><BookingDetails /></PublicLayout>} />

                  {/* Customer account */}
                  <Route path="/account/bookings" element={<PublicLayout><MyBookings /></PublicLayout>} />
                  <Route path="/account/bookings/:id" element={<PublicLayout><BookingDetailPage /></PublicLayout>} />
                  <Route path="/account/wallet" element={<PublicLayout><Wallet /></PublicLayout>} />
                  <Route path="/account/travellers" element={<PublicLayout><SavedTravellers /></PublicLayout>} />
                  <Route path="/account/wishlist" element={<PublicLayout><WishlistPage /></PublicLayout>} />
                  <Route path="/wishlist" element={<PublicLayout><WishlistPage /></PublicLayout>} />

                  {/* Public sharable trip view */}
                  <Route path="/trip/share/:token" element={<PublicLayout><TripShare /></PublicLayout>} />

                  {/* Legal & info pages */}
                  <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />
                  <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
                  <Route path="/refund-policy" element={<PublicLayout><Refund /></PublicLayout>} />
                  <Route path="/cookie-policy" element={<PublicLayout><Cookie /></PublicLayout>} />
                  <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
                  <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                  <Route path="/faq" element={<PublicLayout><Faq /></PublicLayout>} />
                  <Route path="/help" element={<PublicLayout><Faq /></PublicLayout>} />
                  </Routes>
                </ErrorBoundary>
                <Toaster />
                <SonnerToaster />
              </BrowserRouter>
            </AccountProvider>
            </CurrencyProvider>
          </CustomerAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
